import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { customersUrl, customerByIdUrl, branchesUrl } from '../../BaseURL';
import Modal from '../../components/Modal';
import { fmtDate } from '../../utils/format';
import { toastSuccess, swalDangerConfirm, showAxiosError } from '../../utils/swal';

const empty = {
  full_name: '',
  gender: '',
  phone: '',
  address: '',
  company: '',
  email: '',
  notes: '',
  branch_id: '',
};

const GENDERS = [
  { key: 'male',   label: 'Male',   icon: '♂' },
  { key: 'female', label: 'Female', icon: '♀' },
  { key: 'other',  label: 'Other',  icon: '⚧' },
];

// Two-letter initials from a full name for the round avatar in the list.
const initialsOf = (name) => {
  const parts = String(name || '').trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '?';
};

// Stable colour per name so the same customer always gets the same avatar tint.
const AVATAR_TONES = ['brand', 'accent', 'success', 'warn', 'violet', 'rose'];
const toneFor = (name) => {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length];
};

const Customers = () => {
  const [me] = useState(() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; } });
  const isSuper = me?.role === 'super_admin';

  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [search, setSearch] = useState('');
  const [filterGender, setFilterGender] = useState('all');
  const [filterBranch, setFilterBranch] = useState('all'); // super admin only
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (branchId = filterBranch) => {
    setLoading(true);
    try {
      const url = isSuper && branchId && branchId !== 'all'
        ? `${customersUrl}?branch_id=${branchId}`
        : customersUrl;
      const res = await axios.get(url);
      if (res.data?.success) setRows(res.data.results || []);
    } catch (err) { showAxiosError(err, 'Could not load customers'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Load branches for the filter dropdown + create-modal picker (super admin only).
  useEffect(() => {
    if (!isSuper) return;
    axios.get(branchesUrl)
      .then((r) => setBranches(r.data?.results || []))
      .catch(() => setBranches([]));
  }, [isSuper]);

  // Reload from server when the super admin flips the branch filter.
  useEffect(() => {
    if (!isSuper) return;
    load(filterBranch);
    // eslint-disable-next-line
  }, [filterBranch]);

  const filtered = useMemo(() => {
    let list = rows;
    if (filterGender !== 'all') {
      list = list.filter((r) => (r.gender || 'other') === filterGender);
    }
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) =>
      [r.full_name, r.phone, r.company, r.address, r.email]
        .some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [rows, search, filterGender]);

  const stats = useMemo(() => ({
    total: rows.length,
    male: rows.filter((r) => r.gender === 'male').length,
    female: rows.filter((r) => r.gender === 'female').length,
    withCompany: rows.filter((r) => (r.company || '').trim().length > 0).length,
  }), [rows]);

  const openCreate = () => {
    // Preselect the branch: managers get their own; super admin defaults to
    // the branch they're filtering on (if any), otherwise leaves it empty so
    // they have to pick one explicitly.
    const defaultBranch = isSuper
      ? (filterBranch !== 'all' ? String(filterBranch) : '')
      : String(me?.branch_id || '');
    setForm({ ...empty, branch_id: defaultBranch });
    setEditId(null); setOpen(true); setError(null);
  };
  const openEdit = (c) => {
    setForm({
      full_name: c.full_name || '',
      gender: c.gender || '',
      phone: c.phone || '',
      address: c.address || '',
      company: c.company || '',
      email: c.email || '',
      notes: c.notes || '',
      branch_id: String(c.branch_id || ''),
    });
    setEditId(c.id); setOpen(true); setError(null);
  };

  const submit = async () => {
    if (!form.full_name.trim()) { setError('Name is required'); return; }
    if (isSuper && !editId && !form.branch_id) {
      setError('Pick which branch this customer belongs to'); return;
    }
    setError(null); setSaving(true);
    try {
      const payload = { ...form };
      if (payload.branch_id === '') delete payload.branch_id;
      if (editId) await axios.patch(customerByIdUrl(editId), payload);
      else await axios.post(customersUrl, payload);
      setOpen(false);
      await load();
      toastSuccess(editId ? 'Customer updated' : 'Customer added');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  const remove = async (c) => {
    const ok = await swalDangerConfirm(
      `Remove ${c.full_name}?`,
      'They will be hidden from POS lookup and invoices. Existing sales history is kept.',
      { confirmButtonText: 'Yes, remove' }
    );
    if (!ok) return;
    try {
      await axios.delete(customerByIdUrl(c.id));
      toastSuccess('Customer removed');
      load();
    } catch (err) { showAxiosError(err); }
  };

  return (
    <>
      {/* Stats strip — reuses the shared prod-stats classes */}
      <div className="prod-stats">
        <div className="prod-stat tone-brand">
          <div className="ic">👥</div>
          <div className="body">
            <div className="lbl">Total customers</div>
            <div className="val">{stats.total}</div>
            <div className="hint">across the catalog</div>
          </div>
        </div>
        <div className="prod-stat tone-accent">
          <div className="ic">🏢</div>
          <div className="body">
            <div className="lbl">Representing a company</div>
            <div className="val">{stats.withCompany}</div>
            <div className="hint">B2B / corporate accounts</div>
          </div>
        </div>
        <div className="prod-stat tone-success">
          <div className="ic">♂</div>
          <div className="body">
            <div className="lbl">Male</div>
            <div className="val">{stats.male}</div>
          </div>
        </div>
        <div className="prod-stat tone-warn">
          <div className="ic">♀</div>
          <div className="body">
            <div className="lbl">Female</div>
            <div className="val">{stats.female}</div>
          </div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <div>
            <h2>Customers</h2>
            <p className="muted-sm" style={{ margin: '2px 0 0' }}>
              People and companies that buy from you. Loaded automatically in POS and used to address invoices.
            </p>
          </div>
          <div className="panel-toolbar">
            {isSuper && (
              <select
                className="f-select"
                value={filterBranch}
                onChange={(e) => setFilterBranch(e.target.value)}
                title="Filter by branch"
                style={{ width: 180 }}
              >
                <option value="all">All branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            )}
            <div className="cust-filter-pills">
              {[
                { k: 'all',    l: 'All',    ct: stats.total },
                { k: 'male',   l: 'Male',   ct: stats.male },
                { k: 'female', l: 'Female', ct: stats.female },
              ].map(({ k, l, ct }) => (
                <button
                  key={k}
                  className={`cust-filter-pill ${filterGender === k ? 'active' : ''}`}
                  onClick={() => setFilterGender(k)}
                >
                  {l} <span className="ct">{ct}</span>
                </button>
              ))}
            </div>
            <div className="prod-search">
              <span className="ic">🔍</span>
              <input
                className="f-input"
                placeholder="Search name, phone, company…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button className="btn-primary" onClick={openCreate}>+ New customer</button>
          </div>
        </div>

        {loading ? (
          <p style={{ color: 'var(--mute)', padding: '24px 0', textAlign: 'center' }}>Loading customers…</p>
        ) : filtered.length === 0 ? (
          <div className="cust-empty">
            <div className="ic">👥</div>
            <h3>{rows.length === 0 ? 'No customers yet' : 'No customers match your filter'}</h3>
            <p>
              {rows.length === 0
                ? 'Add the people and companies you sell to. Their details will be loaded on POS and used to prepare invoices.'
                : 'Try clearing the search or switching the gender filter.'}
            </p>
            {rows.length === 0 && <button className="btn-primary" onClick={openCreate}>+ Add your first customer</button>}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl cust-tbl">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Phone</th>
                  <th>Location</th>
                  <th>Company</th>
                  {isSuper && <th>Branch</th>}
                  <th>Added</th>
                  <th className="right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div className="cust-cell">
                        <div className={`cust-avatar tone-${toneFor(c.full_name)}`}>{initialsOf(c.full_name)}</div>
                        <div className="cust-name-wrap">
                          <div className="cust-name">{c.full_name}</div>
                          <div className="cust-sub">
                            {c.gender && (
                              <span className={`gender-chip ${c.gender}`}>
                                {GENDERS.find((g) => g.key === c.gender)?.icon} {c.gender}
                              </span>
                            )}
                            {c.email && <span className="mute-tiny">{c.email}</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>{c.phone ? <a href={`tel:${c.phone}`} className="link-subtle">{c.phone}</a> : <span className="muted-sm">—</span>}</td>
                    <td>{c.address || <span className="muted-sm">—</span>}</td>
                    <td>
                      {c.company
                        ? <span className="company-chip">🏢 {c.company}</span>
                        : <span className="muted-sm">Individual</span>}
                    </td>
                    {isSuper && (
                      <td>
                        {c.branch_name
                          ? <span className="branch-tag">📍 {c.branch_name}</span>
                          : <span className="muted-sm">—</span>}
                      </td>
                    )}
                    <td className="muted-sm">{fmtDate(c.created_at)}</td>
                    <td className="right">
                      <div className="row-icons">
                        <button className="icon-btn brand" onClick={() => openEdit(c)} title="Edit">
                          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M13 3l4 4-9 9H4v-4l9-9z"/>
                          </svg>
                        </button>
                        <button className="icon-btn danger" onClick={() => remove(c)} title="Remove">
                          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 6h12M8 6V4h4v2M6 6l1 10h6l1-10"/>
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        title={editId ? 'Edit customer' : 'New customer'}
        open={open} onClose={() => setOpen(false)} width={640}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={submit} disabled={saving || !form.full_name.trim()}>
              {saving ? 'Saving…' : (editId ? 'Save changes' : 'Add customer')}
            </button>
          </>
        }
      >
        {error && <div className="notice">{error}</div>}

        <div className="cust-form-preview">
          <div className={`cust-avatar big tone-${toneFor(form.full_name)}`}>
            {initialsOf(form.full_name || '?')}
          </div>
          <div>
            <div className="preview-name">{form.full_name || 'New customer'}</div>
            <div className="preview-meta">
              {form.gender && <span className={`gender-chip ${form.gender}`}>{GENDERS.find((g) => g.key === form.gender)?.icon} {form.gender}</span>}
              {form.company && <span className="company-chip">🏢 {form.company}</span>}
              {!form.gender && !form.company && <span className="muted-sm">Fill in the details below…</span>}
            </div>
          </div>
        </div>

        <div className="f-grid f-grid-2" style={{ marginTop: 16 }}>
          {isSuper && (
            <div className="f-row" style={{ gridColumn: '1 / -1' }}>
              <label className="f-label">Branch *</label>
              <select
                className="f-select"
                value={form.branch_id}
                onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                disabled={!!editId}
              >
                <option value="">— Pick a branch —</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <div className="f-hint">
                {editId
                  ? 'Branch is locked once a customer has been created.'
                  : 'Every customer belongs to exactly one branch. Only that branch will see them in POS.'}
              </div>
            </div>
          )}

          <div className="f-row" style={{ gridColumn: '1 / -1' }}>
            <label className="f-label">Full name *</label>
            <input
              className="f-input"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              placeholder="e.g. Kwame Boateng"
              autoFocus
            />
          </div>

          <div className="f-row" style={{ gridColumn: '1 / -1' }}>
            <label className="f-label">Gender</label>
            <div className="gender-picker">
              {GENDERS.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  className={`gender-opt ${g.key} ${form.gender === g.key ? 'active' : ''}`}
                  onClick={() => setForm({ ...form, gender: form.gender === g.key ? '' : g.key })}
                >
                  <span className="ic">{g.icon}</span>
                  <span>{g.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="f-row">
            <label className="f-label">Telephone</label>
            <input
              className="f-input"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="0244 000 000"
              inputMode="tel"
            />
          </div>

          <div className="f-row">
            <label className="f-label">Email <span className="muted-sm">(optional)</span></label>
            <input
              className="f-input"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="name@example.com"
            />
          </div>

          <div className="f-row" style={{ gridColumn: '1 / -1' }}>
            <label className="f-label">Location / address</label>
            <input
              className="f-input"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="e.g. East Legon, Accra"
            />
          </div>

          <div className="f-row" style={{ gridColumn: '1 / -1' }}>
            <label className="f-label">Company represented <span className="muted-sm">(if any)</span></label>
            <input
              className="f-input"
              value={form.company}
              onChange={(e) => setForm({ ...form, company: e.target.value })}
              placeholder="e.g. Seraphim Ltd. — leave blank for individuals"
            />
            <div className="f-hint">
              This is the company or organisation the buyer represents. It shows on invoices addressed to them.
            </div>
          </div>

          <div className="f-row" style={{ gridColumn: '1 / -1' }}>
            <label className="f-label">Notes <span className="muted-sm">(optional)</span></label>
            <textarea
              className="f-textarea"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Prefers M-Pesa · Buys in bulk on Fridays"
              style={{ resize: 'vertical' }}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Customers;
